import { z } from 'zod';
/**
 * **Contrats d'entrée de `CDC-4` — les espaces persistants d'une personne.**
 *
 * Écrits une fois ici, ils valident l'entrée de l'API et valideraient les mêmes
 * formulaires côté front sans être réécrits (`DOCTRINE.md` §4). `strictObject`
 * partout : une clé inconnue produit un 400 visible, elle ne disparaît pas en
 * silence — asymétrie voulue avec la sortie, où un champ non déclaré est retiré sans
 * rien dire (D-021).
 *
 * **Chaque champ porte un `.describe()`** — seul `.describe()` atteint OpenAPI, donc
 * le client généré, donc l'autocomplétion de qui écrira le LOT-4b (D-024).
 *
 * **CE DOCUMENT N'A QUE TROIS ROUTES QUI PRENNENT UN CORPS**, et six qui ne prennent
 * rien : ses surfaces **lisent** ce que les trois lots précédents ont écrit. C'est ce
 * que `CDC-0` §9 annonce — « **ne crée qu'une entité**, lit les objets des trois
 * précédents et leur donne leurs surfaces ».
 */
/** Identifiant opaque d'une ligne. Jamais interprété côté client. */
const identifiant = z.string().trim().min(1).max(64);
// ---------------------------------------------------------------------------
// F-46 — l'historique
// ---------------------------------------------------------------------------
/**
 * `GET /api/v1/historique` — F-46.
 *
 * **AUCUN `sort` NI `order`, ET LEUR ABSENCE EST LA RÈGLE.** `DOCTRINE.md` §5 pose
 * une forme unique de liste qui les porte tous deux ; F-46 tranche l'inverse et c'est
 * `docs/cdc/` qui fait autorité sur les contrats — « une ligne par cours, du plus
 * récent au plus ancien, **ordre unique et non réglable** » (DD-33), parce qu'« un
 * tri réglable ne décide rien de plus et coûte un état de plus à chaque écran ».
 * **Les offrir au contrat les rendrait exerçables**, et un front les brancherait.
 *
 * **AUCUN FILTRE PAR DATE, PAR STRUCTURE NI PAR ÉTAT** non plus, F-46 : « on cherche
 * par le prénom d'un participant et par le titre du cours, **et rien d'autre** : ce
 * sont les deux seules valeurs que la ligne porte et que la personne connaît ».
 *
 * **Le curseur reste par curseur et non par numéro de page**, ce que `DOCTRINE.md` §5
 * impose et que F-46 ne contredit pas : « la liste se charge par tranches, la plus
 * récente d'abord, **sans plafond de nombre de cours** ». Rien ne se tronque — « une
 * ligne se conserve pour toujours, et un plafond d'affichage rendrait inatteignable
 * ce que le produit garde exprès ».
 */
export const historiqueQuerySchema = z.strictObject({
    suite: z
        .string()
        .trim()
        .min(1)
        .max(128)
        .optional()
        .describe('Le curseur rendu par la tranche précédente. OPAQUE : il ne se construit ' +
        'pas et ne s’interprète pas côté client. Absent, on obtient la tranche ' +
        'la plus récente.'),
    combien: z.coerce
        .number()
        .int()
        .min(1)
        .max(100)
        .optional()
        .describe('Combien de lignes au plus. Cinquante par défaut.'),
    recherche: z
        .string()
        .trim()
        .min(1)
        .max(255)
        .optional()
        .describe('Cherche sur LE PRÉNOM D’UN PARTICIPANT et LE TITRE DU COURS, et rien ' +
        'd’autre — ce sont les deux seules valeurs que la ligne porte et que ' +
        'vous connaissez. Il n’existe aucun filtre par date, par structure ni ' +
        'par état.'),
});
// ---------------------------------------------------------------------------
// F-51 — les trois préférences
// ---------------------------------------------------------------------------
/**
 * **Les trois préférences du produit, liste fermée** — `CDC-4` §4.
 *
 * « Trois préférences existent dans le produit, **toutes portées par la personne** :
 * micro et caméra (`CDC-2`), rangement d'un document (`CDC-3`), notification par
 * email de conversation (ici). »
 *
 * **`media` en porte DEUX VALEURS et reste UNE préférence** : `CDC-2` écrit « une
 * préférence **de micro et de caméra** par personne », au singulier, et c'est elle
 * qu'une coupure volontaire met à jour pendant un cours (Q419, Q420).
 *
 * **Une QUATRIÈME valeur existe et n'est PAS ici** : l'exception de notification
 * d'une conversation, portée par la **participation** et non par la personne (Q486).
 * F-51 est catégorique — « elle se règle **DANS la conversation**, pas ici » —, et sa
 * route appartient au chat, `PUT /api/v1/conversations/:id/notification`.
 */
export const NOMS_DE_PREFERENCE = ['media', 'rangement', 'notification'];
export const nomDePreferenceSchema = z
    .enum(NOMS_DE_PREFERENCE)
    .describe('Laquelle des trois préférences du produit. Liste fermée : media (micro et ' +
    'caméra), rangement (où se range un document fabriqué), notification ' +
    '(email à la réception d’un message).');
/**
 * `PUT /api/v1/reglages/preferences/:nom` — F-51.
 *
 * **UN SEUL CORPS POUR TROIS PRÉFÉRENCES, ET C'EST LE CHEMIN QUI DÉCIDE.** Le
 * contrat de `CDC-4` §7 déclare **une** route paramétrée par `:nom`, pas trois
 * routes ; le corps porte donc les champs des trois, tous facultatifs, et
 * `champsAttendus()` ci-dessous dit lesquels vont avec quel nom. **Un champ envoyé
 * qui n'appartient pas à la préférence nommée est REFUSÉ** et non ignoré : ignorer
 * ferait croire que le réglage a pris.
 *
 * **L'écran n'est pas le seul chemin, et le geste continue de valoir** (Q493) : « sans
 * écran, il faudrait **entrer dans un cours** pour changer l'état de sa caméra et
 * **fabriquer un document** pour changer où il se range. Le geste reste le chemin
 * court, l'écran est le chemin qui existe hors de l'occasion. »
 */
export const reglagePreferenceSchema = z.strictObject({
    micro: z
        .boolean()
        .optional()
        .describe('Préférence `media`. Micro ouvert à chaque entrée dans un cours. Couper ' +
        'ou rallumer PENDANT un cours met aussi cette valeur à jour ; une ' +
        'coupure IMPOSÉE par le tuteur ne la touche pas (DD-16).'),
    camera: z
        .boolean()
        .optional()
        .describe('Préférence `media`. Caméra allumée à chaque entrée dans un cours. Même ' +
        'règle que le micro sur la mise à jour et sur la coupure imposée.'),
    dossierId: identifiant
        .nullish()
        .describe('Préférence `rangement`. Le dossier de VOTRE espace où se range un ' +
        'document fabriqué. `null` retire la préférence : le document se range ' +
        'alors là où le produit le range par défaut.'),
    emailDeConversation: z
        .boolean()
        .optional()
        .describe('Préférence `notification`. Recevoir un email à la réception d’un ' +
        'message. C’est le réglage GÉNÉRAL ; une conversation peut porter une ' +
        'exception qui le surcharge, et elle se règle dans la conversation.'),
});
/**
 * **Quels champs appartiennent à quelle préférence.** Règle pure, donc testable sans
 * base : c'est la catégorie 2 du plan de test.
 */
export const champsAttendus = (nom) => {
    switch (nom) {
        case 'media':
            return ['micro', 'camera'];
        case 'rangement':
            return ['dossierId'];
        case 'notification':
            return ['emailDeConversation'];
    }
};
// ---------------------------------------------------------------------------
// F-52 — la fermeture globale des connexions
// ---------------------------------------------------------------------------
/**
 * `DELETE /api/v1/reglages/connexions` — F-52, corps déclaré par `CDC-4` §7.
 *
 * **IL N'EXISTE PAS DE FERMETURE LIGNE PAR LIGNE, ET C'EST UNE DÉCISION.**
 * « Distinguer une connexion étrangère d'une des siennes sur une adresse réseau et un
 * nom d'appareil **n'est pas quelque chose qu'une personne sait faire** ; un geste
 * unique est **actionnable sans interprétation**. » Ce schéma ne porte donc aucun
 * identifiant de session, et lui en ajouter un serait rouvrir la décision.
 *
 * **C'est le seul levier du produit contre une session reprise** : « une session vaut
 * sept jours : sans ce geste, la reprise durait sept jours quoi qu'il arrive ».
 */
export const fermetureConnexionsSchema = z.strictObject({
    motDePasseCourant: z
        .string()
        .min(1)
        .max(200)
        .describe('Votre mot de passe courant, exigé. Ce geste ferme TOUTES vos connexions ' +
        'sauf celle d’où vous agissez, et il n’y a pas de fermeture ligne par ' +
        'ligne.'),
});
//# sourceMappingURL=espaces.contract.js.map